// The Results column: stage rail while the run works, then ONE reveal of every card at once.
//
// Selector is `ets-maven-results` (ruling L20) — `wt-` is the file-name convention, not the selector
// prefix. Lane S6 owns the chassis and places this tag; this component never edits the module or the
// panel template.
//
// The design of record: https://claude.ai/code/artifact/769bae85-574d-4ec4-b72e-33d3e9147479, plus
// rulings L79-L84 on the coordination board. Cited by URL and ruling, never by revision number or
// date — a pinned one goes stale silently and still reads as precision. The URL follows the drawing
// across republishes; a ruling that is replaced says so in its own row.
//
// The drawing is authoritative for the CONTENT of this column. The panel frame and the column
// proportions belong to S6: this component sets no column width and no flex basis, so it takes
// whatever track the chassis gives it. (The two widths in its stylesheet are a 7px stage bullet and
// a fit-content sort bar — neither is a frame dimension.)
//
// WHAT THIS COLUMN NEVER SHOWS, and the reason each is absent:
//   - a count, anywhere, on the rail or beside the cards (R-C / L28). A figure the trader can
//     subtract the visible cards from is the reconciliation the no-messages rule exists to prevent;
//   - a reason line, a dropped-day report or an excluded list;
//   - a crossed-out card. A day that did not make it is simply not here;
//   - how many cards the "show top N" setting is holding back.
// The one message it ever shows is the backend's single sentence when a run produced no packages.

import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges
} from '@angular/core';
import {Subscription} from 'rxjs';
import {MavenAppliedMarksService, mavenCardKey} from './maven-applied-marks.service';
import {MavenCardPricingService, MavenLivePrice, MavenNormFactorResolver} from './maven-card-pricing.service';
import {applyMavenDisplayLimit, MavenSortDirection, MavenSortKey, sortMavenCards} from './maven-card-display';
import {MAVEN_OUTCOME_EMPTY, MAVEN_OUTCOME_SUCCEEDED, MavenCard, MavenResultReady} from './maven-delivery.contract';
import {mavenPlaceholderScore} from './maven-card-format';
import {DetectMethodChanges} from '../../utils';

@Component({
    selector: 'ets-maven-results',
    templateUrl: './maven-results.component.html',
    styleUrls: ['./maven-results.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MavenResultsComponent implements OnChanges, OnDestroy {

    /** True from Apply until the one delivery arrives. A second Apply simply starts again. */
    @Input() running = false;

    /** The ONE delivery. Null before the first run of the session. */
    @Input() delivery: MavenResultReady | null = null;

    /** The ORION "show top N" setting. A DISPLAY limit only — never a cut on what arrived. */
    @Input() topN: number | null = null;

    /** Point-value ratio resolver, so an ES leg prices beside an SPX one. */
    @Input() getNormFactor: MavenNormFactorResolver | null = null;

    @Output() cardPicked = new EventEmitter<MavenCard | null>();

    selectedKey: string | null = null;

    sortKey: MavenSortKey = 'perDay';
    sortDirection: MavenSortDirection = 'desc';

    private _ordered: MavenCard[] = [];
    private _live: { [cardKey: string]: MavenLivePrice } = {};

    /** ⚠ PLACEHOLDER scores by card key, set once per delivery — see mavenPlaceholderScore. */
    private _scores: { [cardKey: string]: number | null } = {};
    private _priceSubscription: Subscription | null = null;

    // ⚠ THIS COLUMN DOES NOT REPAINT THE MARKS. Each card reads the A/H marks from the store and
    // runs its OWN change detection on a change (maven-card.component.ts) — a `detectChanges()` here
    // did not reach the OnPush cards reliably under the detached panel (vrequest 2026-09-13, second
    // pass). The store is still injected here for one reason: a new delivery resets it.
    constructor(
        private readonly _pricing: MavenCardPricingService,
        private readonly _marks: MavenAppliedMarksService,
        // ⚠ NAMED `_changeDetector` ON PURPOSE: `@DetectMethodChanges()` reads this field by that
        // string (utils.ts:450). Rename it and the decorator on onSort() silently detects nothing.
        private readonly _changeDetector: ChangeDetectorRef
    ) {
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.delivery) {
            this.onDelivery();
        }
    }

    ngOnDestroy(): void {
        this._pricing.detach();

        if (this._priceSubscription !== null) {
            this._priceSubscription.unsubscribe();
            this._priceSubscription = null;
        }
    }

    /** The cards to draw: the trader's order, then the display limit. */
    get visibleCards(): MavenCard[] {
        return applyMavenDisplayLimit(this._ordered, this.topN);
    }

    get hasCards(): boolean {
        return this._ordered.length > 0;
    }

    /**
     * The single sentence a run with no packages shows — the backend's own words, and the only
     * message this column ever displays.
     */
    get emptySentence(): string | null {

        if (this.running || !this.delivery) {
            return null;
        }

        if (this.delivery.outcome === MAVEN_OUTCOME_SUCCEEDED && this.hasCards) {
            return null;
        }

        if (this.delivery.outcome === MAVEN_OUTCOME_EMPTY || !this.hasCards) {
            return this.delivery.failureDetail || null;
        }

        return null;
    }

    /**
     * CR-20260914-001, the scoped exception to R-C: the dates on which a side could not be hedged
     * to the expected range, one line each, as the backend worded them. Shown above the cards, or
     * under the one sentence when nothing survived. Empty = nothing to say. No other dropped-day
     * reason is ever rendered here.
     */
    get expectedRangeNotes(): string[] {
        if (this.running || !this.delivery || !this.delivery.expectedRangeNotes) {
            return [];
        }

        return this.delivery.expectedRangeNotes.filter(x => !!x);
    }

    get defaultQty(): number {
        return this.delivery ? this.delivery.defaultQty : 0;
    }

    liveFor(card: MavenCard): MavenLivePrice | null {
        const found = this._live[mavenCardKey(card)];

        return found === undefined ? null : found;
    }

    /** The card's placeholder score, fixed at delivery. Null when it has none. */
    scoreFor(card: MavenCard): number | null {
        const found = this._scores[mavenCardKey(card)];

        return (found === null || found === undefined) ? null : found;
    }

    isSelected(card: MavenCard): boolean {
        return this.selectedKey !== null && this.selectedKey === mavenCardKey(card);
    }

    trackCard(_index: number, card: MavenCard): string {
        return mavenCardKey(card);
    }

    /**
     * Re-sort on the trader's request — and ONLY on the trader's request.
     *
     * A price tick calls `onLivePrices`, which never touches this order (A3 / guarantee G8). The
     * rank badges are read from the cards themselves, so they stay put through every re-sort: the
     * row's position and the card's number are different facts.
     *
     * ⚠ `@DetectMethodChanges()`, NOT `markForCheck()` (vrequest 2026-09-13, the "very long to
     * switch" side bug). `markForCheck()` only flags the view; it relies on a zone tick that a click
     * inside the DevExtreme scroll view does not reliably produce, so the new order sat unpainted
     * until an unrelated event swept it up. The ORION list hit the identical fault on 2026-09-07 and
     * the decorator — which calls `detectChanges()` on `this._changeDetector` — is the codebase's
     * answer.
     */
    @DetectMethodChanges()
    onSort(key: MavenSortKey): void {

        if (this.sortKey === key) {
            this.sortDirection = this.sortDirection === 'desc' ? 'asc' : 'desc';
        } else {
            this.sortKey = key;
            this.sortDirection = 'desc';
        }

        this._ordered = sortMavenCards(this._ordered, this.sortKey, this.sortDirection);
    }

    /** The caret beside the ACTIVE key. Null on the other key — the slot stays, the glyph does not. */
    sortArrowClass(key: MavenSortKey): string | null {

        if (this.sortKey !== key) {
            return null;
        }

        return this.sortDirection === 'desc' ? 'fas fa-caret-down' : 'fas fa-caret-up';
    }

    onCardPicked(card: MavenCard): void {
        this.selectedKey = mavenCardKey(card);
        this.cardPicked.emit(card);
        this._changeDetector.markForCheck();
    }

    private onDelivery(): void {

        // A new run's packages are new packages. Marks from the previous one would attach a sent
        // order to something that no longer exists.
        this._marks.reset();

        this.selectedKey = null;
        this._live = {};
        this.cardPicked.emit(null);

        const cards = (this.delivery && this.delivery.cards) ? this.delivery.cards : [];

        // The delivered order IS the run's ranking, best credit per day first. It is taken as given
        // and never recomputed here — what the run ranked first, the column shows first.
        this._ordered = cards.slice();
        this.sortKey = 'perDay';
        this.sortDirection = 'desc';

        // The placeholder scores, computed ONCE per delivery and keyed by card, so a re-sort or a
        // tick never changes a card's number (owner: fixed per card, ordered with cost per day).
        this._scores = {};

        const best = cards.reduce((max, card) => {
            const perDay = card?.prices?.perDay;

            return (perDay === null || perDay === undefined || perDay <= max) ? max : perDay;
        }, 0);

        cards.forEach(card => {
            this._scores[mavenCardKey(card)] = mavenPlaceholderScore(card?.prices?.perDay, best);
        });

        if (this._priceSubscription === null) {
            this._priceSubscription = this._pricing.prices.subscribe(prices => this.onLivePrices(prices));
        }

        if (this._ordered.length === 0) {
            this._pricing.detach();
            this._changeDetector.markForCheck();
            return;
        }

        this._pricing.attach(this._ordered, {
            defaultQty: this.defaultQty,
            getNormFactor: this.getNormFactor || undefined
        });

        this._changeDetector.markForCheck();
    }

    /**
     * A tick arrived. It replaces figures and NOTHING else — no sort, no re-rank, no re-selection.
     */
    private onLivePrices(prices: MavenLivePrice[]): void {

        const next: { [cardKey: string]: MavenLivePrice } = {};

        (prices || []).forEach(price => {
            next[price.cardKey] = price;
        });

        this._live = next;

        // The prices arrive off the message bus, outside anything Angular is watching, so this
        // OnPush component has to be told. markForCheck alone would wait for a zone tick that may
        // never come.
        this._changeDetector.detectChanges();
    }
}
